import { ITriggerNodeTriggerData } from "./ITriggerNodeTrigger"
import { useProxyWatch, Log } from "@fanfanlo"
import { useTranslation } from '@fanfanlo'
import { Box, Stack, Typography, Paper, Chip } from "@mui/material"
import { SelectorOperationItem } from "./selector-chain"

const fileLog = new Log(false, 'NodeTriggerUI_f')
fileLog.pause = true

/**
 * NodeTrigger UI 组件
 * 显示包名、Activity名、类名和节点选择器链(只读)
 */
export function UI(data: ITriggerNodeTriggerData) {
    const { t } = useTranslation('droid/project-v2-ext/triggers/a11y/node-trigger/content')
    fileLog.log('UI rendered, data =', data)

    const [pn] = useProxyWatch(data, "javaData.pn", data.javaData.pn)
    const [an] = useProxyWatch(data, "javaData.an", data.javaData.an)
    const [cn] = useProxyWatch(data, "javaData.cn", data.javaData.cn)
    const [selectorChain] = useProxyWatch(data, "javaData.selectorChain", data.javaData.selectorChain)

    fileLog.log('pn =', pn)
    fileLog.log('an =', an)
    fileLog.log('cn =', cn)
    fileLog.log('selectorChain operations count =', selectorChain?.operations?.length || 0)

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="b">
                    {t('UI.title')}
                </Typography>
            </Box>

            <Stack direction="column" spacing={2}>
                {/* 包名 */}
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {t('UI.packageName')}
                    </Typography>
                    <Typography variant="body1">
                        {pn || t('UI.notSet')}
                    </Typography>
                </Box>

                {/* Activity 名 */}
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {t('UI.activityName')}
                    </Typography>
                    <Typography variant="body1">
                        {an || t('UI.notSet')}
                    </Typography>
                </Box>

                {/* 类名 (可选) */}
                {cn && (
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            {t('UI.className')}
                        </Typography>
                        <Typography variant="body1">
                            {cn}
                        </Typography>
                    </Box>
                )}

                {/* 选择器链 */}
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('UI.selectorOperations', { count: selectorChain?.operations?.length || 0 })}
                    </Typography>
                    {selectorChain && selectorChain.operations && selectorChain.operations.length > 0 ? (
                        <Stack direction="column" spacing={1}>
                            {selectorChain.operations.map((operation, index) => (
                                <OperationItem key={index} operation={operation} index={index} />
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="body2" color="text.disabled">
                            {t('UI.noOperations')}
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Box>
    )
}

/**
 * 单个选择器操作的显示组件
 */
function OperationItem({ operation, index }: { operation: SelectorOperationItem; index: number }) {
    const { t } = useTranslation('droid/project-v2-ext/triggers/a11y/node-trigger/content')
    fileLog.log(`OperationItem ${index} rendered, operation =`, operation)

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('UI.operationIndex', { index: index + 1 })}
            </Typography>

            <Stack direction="row" spacing={1}>
                <Chip
                    label={t('UI.method', { method: operation.method })}
                    size="small"
                    color="primary"
                />
                <Chip
                    label={t('UI.value', { value: JSON.stringify(operation.value) })}
                    size="small"
                />
            </Stack>
        </Paper>
    )
}
